import { useState } from 'react';
import type { FieldMetadata, ObjectMetadata } from '../../../data/client';
import { BottomSheet, Button } from '../../../ui';
import { FilterBuilder } from '../../filters/FilterBuilder';
import { isFilterableField, type RelationContext } from '../../filters/filterModel';
import type { ViewState } from '../views/viewState';
import { FilterTokenStrip } from './FilterTokenStrip';
import { filterTokens } from './filterTokens';
import type { RecordSegment } from './TableToolbar';
import { useTableControls } from './useTableControls';

/**
 * THE PHONE'S FILTER ROW (wave 5 round 4, B4; spec/screens/list.md section 3 item 2: "Filter
 * tokens as a horizontally scrolling row under the bar; 'Add Filter' opens a sheet at the
 * medium detent, never a popover (rulebook PH4)").
 *
 * WHAT WAS MISSING. At 375 the list had NO tokens. What narrowed it lived one drill-in deep
 * inside the Adjust sheet, as a sentence on a row - so a person who came back to a saved view
 * could not see that it was filtered without opening a sheet and reading a value line. The
 * desktop has had the row since wave 3; this is the same row, under the navigation bar
 * instead of beside the view chip.
 *
 * AND THE CONDITION EDITOR IS A SHEET, NOT A POPOVER. Rulebook PH4 and the spec line above:
 * a popover anchored to a control on a phone is a desktop pattern with nowhere to point. It
 * is the MEDIUM detent (`ui/Dialog`'s `detent`), because one condition is one short question
 * and the list it narrows is the context for the answer - the kit's own
 * `Sheets/Light/iPhone/Medium Detent` leaves that list visible above it.
 *
 * THE EDITOR ITSELF IS THE DESKTOP'S. `FilterBuilder` already lays its conditions out as a
 * stack below the split, which is why it needs no phone variant - see its own note on
 * `block`.
 */
export interface PhoneFilterRowProps {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  readonly relations: RelationContext;
  readonly state: ViewState;
  readonly onStateChange: (next: (current: ViewState) => ViewState) => void;
  readonly segment: RecordSegment;
  readonly onSegmentChange: (next: RecordSegment) => void;
}

export function PhoneFilterRow({
  object,
  fields,
  relations,
  state,
  onStateChange,
  segment,
  onSegmentChange,
}: PhoneFilterRowProps) {
  const { setFilter } = useTableControls({ object, fields, state, onStateChange });
  const [sheetOpen, setSheetOpen] = useState(false);

  const tokens = filterTokens(state.filter, fields, relations);

  /**
   * The same rule the desktop capsule follows: a control that opens an editor with no fields
   * in it is a dead end. The related anchors count, because "agencies with at least one deal"
   * is a question about an object whose own fields might all be currency.
   */
  const filterable =
    fields.some(isFilterableField) || (relations.anchorsOf(object.id) ?? []).length > 0;

  // Nothing to say and nothing to offer: no row at all, rather than an empty strip with a
  // button that opens a dead end.
  if (tokens.length === 0 && segment !== 'archived' && !filterable) return null;

  return (
    <div className="list-capsules list-capsules-phone">
      <FilterTokenStrip
        tokens={tokens}
        filter={state.filter}
        onFilterChange={setFilter}
        segment={segment}
        onSegmentChange={onSegmentChange}
      />
      {filterable && (
        <>
          <Button variant="push" size="sm" icon="plus" onClick={() => setSheetOpen(true)}>
            Add filter…
          </Button>
          <BottomSheet
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            detent="medium"
            title="Add filter"
            dismissLabel={false}
          >
            {/* Mounted only while the sheet is open, which is what seeds and drops the
                builder's draft - see the lifetime note in FilterBuilder. */}
            {sheetOpen && (
              <FilterBuilder
                /* THE STACK, NOT THE ONE-LINE CONDITION (`ui-foundation.md` D11). The desktop
                   lays a condition out as one 40px row - field, operator, value, remove - and
                   at 375 those four controls squeeze to "is one of" over three lines beside a
                   truncated operand. `block` is the layout the Adjust sheet's own Filter page
                   already uses for the same editor on the same screen width. */
                layout="block"
                objectId={object.id}
                fields={fields}
                value={state.filter}
                onChange={setFilter}
                onDone={() => setSheetOpen(false)}
              />
            )}
          </BottomSheet>
        </>
      )}
    </div>
  );
}
